import { Button } from '@/components/ui/primitives';
import {
    CtaBand,
    ContextNote,
    FAQList,
    FeatureGrid,
    Hero,
    MarketingPage,
    PageFooter,
    StepList,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/measure-area-pdf');

const STEPS = [
    {
        title: 'Calibrate the PDF scale',
        body: 'Choose two points on a known length, enter its real-world measurement, and save the scale. See the calibration guide below for the steps.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A calibrated dimension line on a floor plan',
    },
    {
        title: 'Outline the room or region',
        body: 'Draw a polygon around an irregular room, or use a rectangle or ellipse for a matching shape. Polygon and rectangle measurements include area and perimeter; ellipses include area.',
        image: '/tool-shots/polygon.jpg',
        alt: 'A polygon markup outlining a room on a floor plan',
    },
    {
        title: 'Read the scaled measurement',
        body: 'With a scale set, the markup list shows calculated area and perimeter for polygons and rectangles, and area for ellipses. Measurements use the selected imperial or metric units.',
        image: '/tool-shots/rectangle.jpg',
        alt: 'A rectangle markup around a room on a PDF plan',
    },
    {
        title: 'Export the markup list',
        body: 'Export the markup list to CSV to carry its computed area and perimeter values with the other measurement columns.',
        image: '/tool-shots/dimension.jpg',
        alt: 'Measurement markups on a floor plan',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Can I get square footage from a PDF floor plan?',
        answer: ['Yes. Calibrate the drawing, then draw a polygon, rectangle, or ellipse. The markup list calculates the area using the saved scale and selected units.'],
    },
    {
        question: 'Which shapes calculate area?',
        answer: ['Polygons, rectangles, and ellipses calculate area. Polygons and rectangles also calculate perimeter.'],
    },
    {
        question: 'Can I measure an irregular room?',
        answer: ['Use a polygon and place its points along the room boundary. The polygon measurement includes area and perimeter after the PDF is calibrated.'],
    },
    {
        question: 'Does the PDF need a scale embedded?',
        answer: ['No. Set a scale in the tool using two points on a known length and its real-world measurement. Check the calibration against another known dimension before measuring.'],
    },
] as const;

export function MeasureAreaPdfPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="Measure area on a PDF"
                    title={<>Measure area on a PDF floor plan.<br />See area and perimeter.</>}
                    lead="Calibrate the drawing, outline rooms or regions, and read scaled area measurements in imperial or metric units."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF plan</Button>}
                    chips={['Area and perimeter', 'Imperial and metric', 'CSV export']}
                />
                <ContextNote text="Open a PDF from your device and measure without uploading the file or creating an account." />
                <StepList title="How to measure floor plan area" lead="Set the scale before drawing a room boundary so the measurements use real-world units." steps={STEPS} />
                <FeatureGrid
                    sectionTitle="Area measurements from PDF markups"
                    sectionLead="Choose the shape that follows the region you need to measure."
                    cards={[
                        { title: 'Irregular rooms', body: 'Use a polygon and place points along the room boundary. Its calculated measurements include area and perimeter.' },
                        { title: 'Rectangles and ellipses', body: 'Rectangles calculate area and perimeter. Ellipses calculate area.' },
                        { title: 'CSV measurements', body: 'Export the markup list as CSV with computed length, area, and perimeter columns.' },
                    ]}
                />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Measure a room on your PDF plan"
                    lead="Calibrate a known length, outline the region, and read its scaled measurement."
                    actions={<><Button href="/" variant="primary" size="lg">Open a PDF plan</Button><Button href="/calibrate-pdf-scale/" variant="ghost" size="lg">Calibrate PDF scale</Button><Button href="/measure-pdf-online/" variant="ghost" size="lg">Measure PDF distances</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Area measurement from calibrated PDF floor plans" />
        </MarketingPage>
    );
}
